import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { PageHeader } from '../components/Layout';
import { Button, Card, KV, Pill } from '../components/ui';
import { relativeTime } from '../lib/format';
import { COLORS } from '../theme';

interface Alert {
  id: string;
  severity: string;
  category: string;
  title: string;
  message: string;
  metadata?: Record<string, unknown>;
  created_at: string;
  acknowledged_at?: string | null;
}

interface DetectResult {
  scannedAt: string;
  createdCount: number;
  suppressedCount: number;
  created: Alert[];
  suppressed: Alert[];
}

export default function AlertsPage() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    // Open alerts come back embedded in admin-metrics. Reuse that endpoint
    // rather than adding a duplicate list endpoint on the server.
    const { data, error } = await supabase.functions.invoke<{ alerts: Alert[] }>('admin-metrics', { body: {} });
    if (error || !data) {
      setError(error?.message ?? 'Failed to load alerts.');
    } else {
      setAlerts(data.alerts ?? []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const scan = useCallback(async () => {
    setScanning(true);
    setScanResult(null);
    const { data, error } = await supabase.functions.invoke<DetectResult>('admin-detect-alerts', { body: {} });
    setScanning(false);
    if (error || !data) {
      setScanResult(error?.message ?? 'Scan failed.');
      return;
    }
    setScanResult(`Scan complete — ${data.createdCount} created, ${data.suppressedCount} suppressed.`);
    void load();
  }, [load]);

  return (
    <div>
      <PageHeader
        title="Alerts"
        right={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {scanResult && <span style={{ color: COLORS.textMuted, fontSize: 12 }}>{scanResult}</span>}
            <Button onClick={scan} disabled={scanning}>{scanning ? 'Scanning…' : 'Run detection'}</Button>
            <Button variant="ghost" onClick={() => { setLoading(true); void load(); }}>Refresh</Button>
          </div>
        }
      />

      {loading && <div style={{ color: COLORS.textMuted, padding: 24 }}>Loading…</div>}
      {error   && <div style={{ color: COLORS.critical, padding: 24 }}>{error}</div>}

      {!loading && !error && alerts.length === 0 && (
        <Card>
          <div style={{ color: COLORS.textMuted, fontSize: 13, padding: '12px 0' }}>
            No open alerts. Run detection to scan the last 24h for regressions.
          </div>
        </Card>
      )}

      {alerts.map((a) => (
        <Card key={a.id} style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
            <SeverityBar severity={a.severity} />
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                <Pill color={severityColor(a.severity)}>{a.severity}</Pill>
                <Pill>{a.category}</Pill>
                <span style={{ color: COLORS.textMuted, fontSize: 11, marginLeft: 'auto' }}>
                  {relativeTime(a.created_at)}
                </span>
              </div>
              <div style={{ color: COLORS.text, fontWeight: 500, marginBottom: 4 }}>{a.title}</div>
              <div style={{ color: COLORS.textSecondary, fontSize: 13, lineHeight: 1.5 }}>{a.message}</div>
              {a.metadata && Object.keys(a.metadata).length > 0 && (
                <div style={{ marginTop: 10 }}>
                  {Object.entries(a.metadata).slice(0, 6).map(([k, v]) => (
                    <KV key={k} k={k} v={String(v)} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

function SeverityBar({ severity }: { severity: string }) {
  return <div style={{ width: 3, alignSelf: 'stretch', background: severityColor(severity), borderRadius: 2 }} />;
}

function severityColor(severity: string): string {
  switch (severity.toUpperCase()) {
    case 'CRITICAL': return COLORS.critical;
    case 'WARN':     return COLORS.warn;
    case 'INFO':     return COLORS.info;
    default:         return COLORS.textSecondary;
  }
}
